import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';
import useSWR from 'swr';

import { AxiosResponse } from 'axios';
import { useRouter } from 'next/router';
import { useNotification } from 'hooks/useNotification';
import { useResponsive } from 'hooks/useResponsive';
import { get, patch, post, remove } from 'lib/utils/http';
import { API_ROUTES } from 'lib/api-routes';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import { RoomAttributes, RoomsResponse } from 'lib/models/candidate-messages';
import { MessageAttributes, MessagesResponse, TouchpointAttributes, TouchpointMessageResponse } from 'lib/models/message';
import { ParticipantAttributes, ParticipantSResponse } from 'lib/models/participant';
import { apiInstance } from 'lib/utils/axios';
import { TBasicStudentSocietyWithPostsStat, TPost } from 'lib/models/discussion';
import { useApp } from 'lib/contexts/save-in-app-context';
import { viewAllPosts } from 'lib/services/discussion';
import { useAuth } from 'lib/providers/AuthProvider';
import { PAGE_ROUTES } from 'lib/page-routes';

export const useCandidateMessageInternal = () => {
  const notificationInstance = useNotification();
  const { headers } = useRecoilValue(httpHeadersState);
  const screens = useResponsive();
  const { isCandidate } = useAuth();
  const [activeRoom, setActiveRoom] = useState<RoomAttributes | null>(null);
  const [selectedInteractedSociety, setSelectedInteractedSociety] = useState<TBasicStudentSocietyWithPostsStat | null>(null);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);

  const { roomData, setRoomData } = useApp();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMessagesFetching, setIsMessagesFetching] = useState<boolean>(false);
  const [participantsCachedData, setParticipantsCachedData] = useState<{
    [key: string]: ParticipantAttributes[];
  }>({});
  const [touchpointCachedData, setTouchpointCachedData] = useState<{
    [key: string]: TouchpointAttributes;
  }>({});
  const touchpointDataRef = useRef<{ [key: string]: TouchpointAttributes }>({});

  const router = useRouter();
  const isMessageRoute = router.pathname === PAGE_ROUTES.CANDIDATE_MESSAGES;

  /** todo check if this API call is needed in other pages as well */
  const {
    data: roomsResponse,
    mutate: mutateRooms,
    isLoading: isRoomsFetching,
  } = useSWR<RoomsResponse>(isCandidate && isMessageRoute ? [API_ROUTES.ROOMS, headers] : null, get);

  const {
    data: interactedSocietiesResponse,
    isLoading: isInteractedSocietiesLoading,
    mutate: mutateInteractedSocitiesResponse,
  } = useSWR<AxiosResponse<Array<TBasicStudentSocietyWithPostsStat>>>(
    isCandidate && isMessageRoute ? API_ROUTES.INTERACTED_STUDENT_SOCIETIES : null,
    apiInstance.get
  );

  const mutateCandidateRooms = async () => {
    await mutateRooms();
  };
  useEffect(() => {
    return () => {
      setRoomData(null);
    };
  }, []);

  const toggleParticipantsModal = useCallback(() => {
    setShowParticipantsModal((showModal) => !showModal);
  }, []);

  const rooms = useMemo(() => {
    const parsedRoomResponse = parseArrayResponse<RoomAttributes>(roomsResponse);
    if (roomData) {
      const existingRoomIndex = parsedRoomResponse.findIndex((room) => room.id === roomData.id);
      let newChatRoom: RoomAttributes = roomData;
      if (existingRoomIndex !== -1) {
        newChatRoom = parsedRoomResponse.splice(existingRoomIndex, 1)[0];
      }
      parsedRoomResponse.unshift(newChatRoom);
    }
    return parsedRoomResponse;
  }, [roomsResponse, roomData]);

  const interactedSocieties = useMemo(() => {
    return interactedSocietiesResponse?.data
      ?.filter((society) => !!society.last_post.id)
      .sort((a, b) => new Date(b.last_post.created_at).getTime() - new Date(a.last_post.created_at).getTime());
  }, [interactedSocietiesResponse]);

  const totalUnreadPostCount = useMemo(() => {
    return interactedSocieties?.reduce((acc, society) => acc + society.not_viewed_count, 0) || 0;
  }, [interactedSocieties]);

  const onUpdateLastPost = useCallback(
    (newPost: TPost) => {
      mutateInteractedSocitiesResponse(
        (prevData) => {
          if (!prevData) return prevData;
          const updatedSocieties = prevData.data.map((society) => {
            if (society.id === selectedInteractedSociety?.id) {
              return {
                ...society,
                last_post: newPost,
              };
            }
            return { ...society };
          });
          return {
            ...prevData,
            data: updatedSocieties,
          };
        },
        {
          revalidate: false,
        }
      );
    },
    [selectedInteractedSociety]
  );

  const onReadPost = useCallback(() => {
    mutateInteractedSocitiesResponse(
      (prevData) => {
        if (!prevData) return prevData;
        const updatedSocieties = prevData.data.map((society) => {
          if (society.id === selectedInteractedSociety?.id) {
            if (society.not_viewed_count > 0) viewAllPosts({ post_id: society.last_post.id });
            return {
              ...society,
              not_viewed_count: society.not_viewed_count > 0 ? 0 : society.not_viewed_count,
            };
          }
          return { ...society };
        });
        return {
          ...prevData,
          data: updatedSocieties,
        };
      },
      {
        revalidate: false,
      }
    );
  }, [selectedInteractedSociety]);

  const onMuteConversation = async (id: string, participable_id: string, isMute: boolean) => {
    const body = {
      participable_id,
    };
    const path = `${API_ROUTES.ROOMS}/${id}/${isMute ? 'unmute' : 'mute'}`;
    try {
      setIsLoading(true);
      await patch(path, body, headers);
      notificationInstance.success({
        title: 'Success!',
        message: `${isMute ? 'Unmuted conversation successfully.' : 'Muted conversation successfully.'}`,
      });
      mutateRooms(
        (prevData) => {
          if (!prevData) return prevData;

          const updatedRooms = prevData.data.map((room) => {
            if (room.id === id) {
              return {
                ...room,
                attributes: {
                  ...room.attributes,
                  muted_at: isMute ? null : new Date().toString(),
                },
              };
            }
            return room;
          });

          return {
            ...prevData,
            data: updatedRooms,
          };
        },
        {
          revalidate: false,
        }
      );
    } catch (error) {
      notificationInstance.handleExceptionError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const onDeleteConversation = async (id: string) => {
    const path = `${API_ROUTES.ROOMS}/${id}`;
    try {
      setIsLoading(true);
      await remove(path, headers);
      notificationInstance.success({
        title: 'Success!',
        message: `Message removed successfully.`,
      });
      await mutateCandidateRooms();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      notificationInstance.handleExceptionError(error as Error);
    }
  };

  const onBlockConversation = async (id: string, participable_id: string, isBlock: boolean) => {
    const body = {
      participable_id,
    };
    const path = `${API_ROUTES.ROOMS}/${id}/${isBlock ? 'unblock' : 'block'}`;
    try {
      setIsLoading(true);
      await post(path, body, headers);
      notificationInstance.success({
        title: 'Success!',
        message: `${isBlock ? 'Unblocked conversation successfully.' : 'Blocked conversation successfully.'}`,
      });
      mutateRooms(
        (prevData) => {
          if (!prevData) return prevData;

          const updatedRooms = prevData.data.map((room) => {
            if (room.id === id) {
              return {
                ...room,
                attributes: {
                  ...room.attributes,
                  blocked: !isBlock,
                },
              };
            }
            return room;
          });

          return {
            ...prevData,
            data: updatedRooms,
          };
        },
        {
          revalidate: false,
        }
      );
    } catch (error) {
      notificationInstance.handleExceptionError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoomMessages = async (roomId: string) => {
    try {
      setIsMessagesFetching(true);
      const response = await onFetchMessages(roomId);
      setIsMessagesFetching(false);
      return response;
    } catch (error) {
      setIsMessagesFetching(false);
      notificationInstance.handleExceptionError(error as Error);
    }
  };

  const onFetchMessages = async (roomId: string) => {
    const responseData = await get<MessagesResponse>(`${API_ROUTES.ROOMS}/${roomId}/messages`, headers);
    return parseArrayResponse<MessageAttributes>(responseData);
  };

  const sendMessage = async (roomId: string, message: string) => {
    const body = {
      message: {
        value: message,
      },
    };
    try {
      await post(`${API_ROUTES.ROOMS}/${roomId}/messages`, body, headers);
      return await onFetchMessages(roomId);
    } catch (error) {
      notificationInstance.handleExceptionError(error as Error);
    }
  };

  const getParticipants = async (roomId: string) => {
    try {
      setIsLoading(true);
      if (participantsCachedData[roomId]) {
        setIsLoading(false);
        return participantsCachedData[roomId];
      } else {
        const response = await get<ParticipantSResponse>(`${API_ROUTES.ROOMS}/${roomId}/participants`, headers);
        const parsedData = parseArrayResponse(response);
        const cachedData = JSON.parse(JSON.stringify(participantsCachedData));
        cachedData[roomId] = parsedData;
        setParticipantsCachedData(cachedData);
        setIsLoading(false);
        return parsedData;
      }
    } catch (error) {
      setIsLoading(false);
      notificationInstance.handleExceptionError(error as Error);
    }
  };

  const fetchTouchpointData = async (slug: string) => {
    const touchpointPath = `${API_ROUTES.CAMPAIGNS}/${slug}`;
    try {
      const touchpointResponse = await get<TouchpointMessageResponse>(touchpointPath, headers);
      const touchpoint = parseResponse<TouchpointAttributes>(touchpointResponse);
      const temp = JSON.parse(JSON.stringify(touchpointDataRef.current));
      temp[slug] = touchpoint;
      touchpointDataRef.current = temp;
      setTouchpointCachedData(temp);
    } catch (error) {
      console.error(error);
    }
  };

  const getTouchpointData = async (slug: string) => {
    if (touchpointCachedData[slug]) {
      return touchpointCachedData[slug];
    } else {
      return await fetchTouchpointData(slug);
    }
  };

  const onRoomSelect = useCallback(
    (room: RoomAttributes) => {
      if (!activeRoom || (activeRoom && activeRoom.id !== room.id)) {
        setActiveRoom(room);
      }
    },
    [activeRoom]
  );

  const onSelectInteractedSociety = useCallback(
    (society: TBasicStudentSocietyWithPostsStat) => {
      if (selectedInteractedSociety?.id !== society.id) {
        setSelectedInteractedSociety(society);
      }
    },
    [selectedInteractedSociety]
  );

  const onClearActiveRoom = useCallback(() => {
    setActiveRoom(null);
  }, []);

  const onClearSelectedInteractedSociety = useCallback(() => {
    setSelectedInteractedSociety(null);
  }, []);

  useEffect(() => {
    const activeRoomAvailable = rooms.find((room) => room.id === activeRoom?.id);
    if (!isRoomsFetching && !activeRoomAvailable && !screens.sm && rooms.length > 0) {
      onRoomSelect(rooms[0]);
    }
  }, [isRoomsFetching, rooms, screens]);

  useEffect(() => {
    const activeSocietyAvailable = interactedSocieties?.find((society) => society.id === selectedInteractedSociety?.id);
    if (!isInteractedSocietiesLoading && !activeSocietyAvailable && !screens.sm && interactedSocieties && interactedSocieties.length > 0) {
      onSelectInteractedSociety(interactedSocieties[0]);
    }
  }, [isInteractedSocietiesLoading, interactedSocieties, screens]);

  return {
    isRoomsFetching,
    isLoading,
    isMessagesFetching,
    rooms,
    interactedSocieties,
    isInteractedSocietiesLoading,
    touchpointCachedData,
    activeRoom,
    selectedInteractedSociety,
    showParticipantsModal,
    totalUnreadPostCount,
    onMuteConversation,
    onDeleteConversation,
    onBlockConversation,
    fetchRoomMessages,
    sendMessage,
    getParticipants,
    fetchTouchpointData,
    getTouchpointData,
    setShowParticipantsModal,
    toggleParticipantsModal,
    onRoomSelect,
    onClearActiveRoom,
    onSelectInteractedSociety,
    onClearSelectedInteractedSociety,
    onUpdateLastPost,
    onReadPost,
  };
};

export type UseCandidateMessageReturnType = ReturnType<typeof useCandidateMessageInternal>;
